import { isEmpty, isNumber, isInt } from "../../utils/index"
import { FloatMath } from "../math/FloatMath"
import { RoundingHelper } from "./RoundingHelper"

function isLarge(n: number) {
  // return Math.abs(n).toString()?.length > 15
  // !!! 有了精度开关, amount可能存在小数
  if (typeof n !== "number") return false
  const str = Math.abs(n).toString().split(".")[0]
  return str.length > 15
}
function sliceNumber(num: string, len: number, separator = ",") {
  let strNum = num.toString()
  let result = ""
  while (strNum.length > 0) {
    const slice = strNum.slice(-len)
    result = slice + (result ? separator : "") + result
    strNum = strNum.slice(0, -len)
  }
  return result
}

function formatNumber(
  value: number,
  format: string,
  separator: string,
  decimal: string
) {
  const sign = Number(value) < 0 ? "-" : ""
  value = Math.abs(value)
  const [integerFormat, decimalFormat] = format.split(".")
  const [integerValue, decimalValue = ""] = value.toString().split(".")
  let formattedInteger = ""
  if (integerFormat.includes(",")) {
    const [_, ...rest] = integerFormat.split(",")
    if (rest?.length > 1) {
      const beginIndex = rest[0]?.length
      const endIndex = integerValue.length - rest[rest.length - 1]?.length
      const firstPart = integerValue.substring(0, endIndex)
      const lastPart = integerValue.substring(endIndex)
      formattedInteger =
        sliceNumber(firstPart, beginIndex, separator) + separator + lastPart
    } else {
      formattedInteger = sliceNumber(integerValue, rest[0]?.length, separator)
    }
  } else {
    formattedInteger = integerValue
  }
  let formattedDecimal = ""
  if (decimalFormat) {
    for (const [index, digit] of Array.from(decimalFormat).entries()) {
      if (digit === "0") {
        formattedDecimal += decimalValue[index] ? decimalValue[index] : "0"
      } else if (digit === "#") {
        if (decimalValue[index]) {
          formattedDecimal += decimalValue[index]
        }
      }
    }
  }
  return (
    sign +
    formattedInteger +
    (formattedDecimal ? decimal : "") +
    formattedDecimal
  )
}

class Money extends RoundingHelper {
  #_money: TMoneyConfig
  #_currencySet: any
  #_moneyConfig: AllMoneyConfig
  static instance: Money
  constructor(config: AllMoneyConfig, defaultCurrency: TCurrency) {
    super()
    if (!new.target) {
      throw new Error("[Money] must be called with new")
    }
    if (Money.instance) {
      return Money.instance
    }
    Money.instance = this
    const keys = Object.keys(config) as TCurrency[]
    if (!config[defaultCurrency]) {
      console.error("Could not find " + defaultCurrency + " from " + JSON.stringify(keys))
      return
    }
    this.#_moneyConfig = config
    this.#_currencySet = new Set(keys)
    this.#_money = config[defaultCurrency]
  }
  /** *
   * 校验币种
   */
  #_verifyCurrency(currency: TCurrency) {
    return this.#_currencySet.has(currency)
  }
  /** *
   * 查找币种对应配置
   */
  #_findCurrency(currency: TCurrency) {
    if (!this.#_verifyCurrency(currency)) {
      console.error("[findCurrency] currency " + currency + " is not supported")
      return {}
    }
    if (this.#_money.currency === currency) return this.#_money
    return this.#_moneyConfig[currency]
  }
  /** *
   * 解析money数据和序列化后的money数据
   */
  #_parseMoney(value: TMoneyValue, hpm = true): TMoney {
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value)
        if (this.isMoney(parsed, hpm)) {
          if (hpm && !Number.isInteger(parsed.amount))
            throw new Error("money amount must be an Integer. " + parsed.amount)
          return parsed
        }
        throw new Error("Invalid serialized money object. " + value)

      } catch (e) {
        throw new Error("parseValue fail. " + e)
      }
    } else if (this.isMoney(value, hpm)) {
      return value
    } else {
      throw new Error(
        "parseMoney, Invalid value type. " + JSON.stringify(value)
      )
    }
  }

  /** *
   * 加减计算逻辑
   */
  #_performOperation(
    type: "add" | "subtract",
    a: TMoneyValue,
    b: TMoneyValue,
    hpm = true
  ) {
    const { amount: A, currency: currencyA } = this.#_parseMoney(a, hpm)
    const { amount: B, currency: currencyB } = this.#_parseMoney(b, hpm)
    if (type === "add") {
      if (A === 0) return { amount: B, currency: currencyB }
      if (B === 0) return { amount: A, currency: currencyA }
    } else {
      if (A === 0) return { amount: -B, currency: currencyB }
      if (B === 0) return { amount: A, currency: currencyA }
    }
    if (currencyA !== currencyB) {
      throw new Error(
        "values must have the same currency. " +
        JSON.stringify(a) +
        JSON.stringify(b)
      )
    }
    const ans = FloatMath[type]?.(A, B)
    return { amount: ans, currency: currencyA }
  }
  /** *
   * 取整/抹零
   */
  #_calculateBase(amount: number, base: number, mode: "floor" | "ceil") {
    if (!amount) {
      throw new Error("[calculateBase] Invalid amount " + amount)
    }
    const [integerPart, decimalPart] = amount?.toString()?.split(".") || []
    const hasIntegerPart = Number(integerPart) && Number(integerPart) > 0
    const hasDecimalPart = Number(decimalPart) && Number(decimalPart) > 0
    if (base > 0) {
      if (base > integerPart?.length) {
        base = integerPart.length
      }
      if (mode === "ceil") {
        // base 大于 0 同时整数位为0，返回 1
        if (!hasIntegerPart) return 1
        // base 大于 0 同时小数位为0，返回 整数位
        if (!hasDecimalPart && base === 1) return amount
        const power = Math.pow(10, base - 1)
        return Math.floor(amount / power) * power + power
      }
      const power = Math.pow(10, base - 1)
      return Math.floor(amount / power) * power

    }
    let absBase = Math.abs(base)
    if (absBase > decimalPart?.length) {
      absBase = decimalPart.length
    }
    if (mode === "ceil") {
      const power = Math.pow(10, absBase)
      const rounded = Math.ceil(amount * power)
      return rounded / power
    }
    const power = Math.pow(10, absBase)
    return Math.floor(amount * power) / power


  }
  /** *
   * 格式化通用能力
   */
  #_format(
    money: TMoney,
    config: {
      pattern: TMoneyConfig["format"]["pattern"]
      symbol: TMoneyConfig["symbol"]
      locale: TMoneyConfig["symbol"]
      cent: TMoneyConfig["cent"]
      separator?: TMoneyConfig["format"]["separator"]
      decimal?: TMoneyConfig["format"]["decimal"]
    }
  ) {
    const {
      pattern,
      symbol,
      locale,
      cent,
      // 配置的pattern规则、用户传入的pattern规则，与separator和decimal无关系，pattern的分割符一定是","小数分割符一定是"." 2024/07/18
      separator = ",",
      decimal = ".",
    } = config
    const { amount, currency } = money
    // 将金额转换为原始单位
    if (!isNumber(amount)) {
      throw new Error("[format] Invalid amount")
    }
    // 检查 amount 是否超过了 15 位
    if (isLarge(amount)) {
      throw new Error("[format] The amount is too large.")
    }
    // 转成主币格式
    let _amount = this.toMajor(money) as number
    let sign = false
    if (pattern.indexOf("-") > -1 && amount < 0) {
      sign = true
      _amount = Math.abs(_amount)
    }
    const replacements: { [key: string]: string } = {
      S: symbol,
      C: currency,
      L: locale,
      "-": sign ? "-" : "",
      " ": " ",
    }
    const formattedNumber = formatNumber(
      _amount,
      pattern.replace(/S|C|L|-|\s+/g, ""),
      separator,
      decimal
    )
    let result = Array.from(pattern)
      .map((char, i) => {
        if (char in replacements) {
          return replacements[char]
        }
        return char

      })
      .join("")
    result = result.replace(
      pattern.replace(/S|C|L|-|\s+/g, ""),
      formattedNumber
    )
    const numIndex = result.indexOf(formattedNumber)
    const prefix = result.substring(0, numIndex) || ""
    const infix = formattedNumber || ""
    const suffix = result.substring(numIndex + formattedNumber.length) || ""
    return { prefix, infix, suffix }
  }
  #_create(
    amount: number,
    currency: TCurrency,
    precision: number,
    cent: number
  ) {
    try {
      amount = Number(amount)
      if (amount === 0)
        return {
          amount: 0,
          currency: currency,
        }
      if (!isInt(amount)) {
        throw new Error("The final amount must be a Integer. " + amount)
      }
      if (isLarge(amount)) {
        throw new Error("The amount is too large.")
      }
      if (!this.#_verifyCurrency(currency)) {
        throw new Error("[create] currency " + currency + " is not supported. ")
      }
      const { round } = this.#_findCurrency(currency)
      amount = this.convertAmount(amount, { precision, cent, round })
      return { amount, currency }
    } catch (e) {
      throw new Error("[create] Invalid value " + e)
    }
  }
  of(money: TMoneyValue, currency = this.#_money.currency, hmp = true) {
    const { strict } = this.#_money
    // 根据配置决定是否抛异常，null、undefined。不支持{money: undefined, currency: ...}， 非法数字)
    // 后续两端抹平后可去掉
    if (!strict && money == undefined) return money
    try {
      // 非高精度模式, 只校验amount是否是数字, currency是否合法
      if (!hmp) {
        if (isNumber(money)) {
          if (!this.#_verifyCurrency(currency))
            throw new Error(currency + " is not supported")
          return { amount: money, currency }
        }
        return this.#_parseMoney(money, hmp)

      }
      if (isEmpty(money)) throw new Error("[of] Invalid money data. " + money)
      const { precision, cent } = this.#_findCurrency(currency) || {}
      if (isInt(money)) {
        return this.#_create(money as number, currency, precision, cent)
      }
      return this.#_parseMoney(money)

      // const count = new Date().valueOf() - time
      // count > 0 && console.log("[of] 耗时 >>>>>>>>>>>", count + "ms")
    } catch (e) {
      throw new Error("[of] " + e)
    }
  }
  ofMajor(num: number, currency = this.#_money.currency, hmp = true) {
    const { strict } = this.#_money
    // 根据配置决定是否抛异常，null、undefined。不支持{money: undefined, currency: ...}， 非法数字)
    // 后续两端抹平后可去掉
    if (!strict && num == undefined) return num

    if (!isNumber(num)) throw new Error("[ofMajor] num must be Number. " + num)

    const { cent, precision, round } = this.#_findCurrency(currency)
    // !!! 精度 + 辅币模式
    // 按照cent处理显示几位，四舍五入
    num = this._toFixed(num, cent, round)
    // 再根据精度转成整数
    num = this.round?.(num * Math.pow(10, precision), round)
    if (!hmp) return { amount: num, currency }
    return this.of(num, currency)
  }
  /** *
   * 是否是money格式数据
   */
  isMoney(obj?: TMoneyValue, hpm = true): obj is TMoney {
    if (obj == null) return false
    try {
      if (typeof obj === "string") {
        obj = JSON.parse(obj)
      }
    } catch (e) {
      console.error("[isMoney] parse Error: ", e)
      return false
    }
    if (typeof obj !== "object") return false
    if (hpm && !isInt(obj.amount)) return false
    if (
      obj.hasOwnProperty("amount") &&
      obj.hasOwnProperty("currency") &&
      this.#_currencySet.has(obj.currency) &&
      // isNumber(obj.amount) &&
      !isLarge(obj.amount)
    )
      return true
    return false
  }
  /** *
   * 判断数据相等
   */
  equals(money1: TMoney | string, money2: TMoney | string) {
    if (!this.isMoney(money1) || !this.isMoney(money2)) {
      console.error("[equals] Invalid money data", money1, money2)
      return false
    }
    money1 = this.#_parseMoney(money1)
    money2 = this.#_parseMoney(money2)
    if (money1.currency !== money2.currency) {
      console.error("[equals] Invalid money currency", money1, money2)
      return false
    }
    return (
      money1.amount === money2.amount && money1.currency === money2.currency
    )
  }
  /** *
   * 排序
   */
  compare(money1: TMoney | string, money2: TMoney | string) {
    if (!this.isMoney(money1) || !this.isMoney(money2)) {
      console.error("[compare] Invalid money data. ", money1, money2)
      return
    }
    money1 = this.#_parseMoney(money1)
    money2 = this.#_parseMoney(money2)
    if (money1.currency !== money2.currency) {
      console.error("[compare] Invalid money currency. ", money1, money2)
      return
    }
    if (money1.amount === money2.amount) return 0
    return money1.amount > money2.amount ? 1 : -1
  }

  /** *
   * 格式化
   */
  format(money: TMoney | string, pattern?: string) {
    const { prefix, infix, suffix } = this.formatParts(money, pattern)
    const result = prefix + infix + suffix
    return result.trim()
  }
  /** *
   * IDE format方法
   */
  formatByConfig(amount: number, config: TMoneyConfig) {
    const {
      currency,
      locale,
      symbol,
      cent,
      precision,
      format: { pattern, decimal, separator },
    } = config
    const _money = this.#_create(amount, currency, precision, cent)
    const { prefix, infix, suffix } = this.#_format(_money, {
      pattern,
      symbol,
      locale,
      cent,
      decimal,
      separator,
    })
    const result = prefix + infix + suffix
    return result.trim()
  }
  /** *
   * 组件format
   */
  formatParts(money: TMoney | string, pattern?: string) {
    const _money = this.#_parseMoney(money)
    const { symbol, locale, cent, format } = this.#_findCurrency(
      _money.currency
    )
    const { separator, decimal } = format
    const _pattern = pattern || format.pattern || this.#_money.format.pattern
    return this.#_format(_money, {
      pattern: _pattern,
      symbol,
      locale,
      cent,
      separator,
      decimal,
    })
  }
  /** *
   * 返回主币种格式
   */
  toMajor(money: TMoney | string) {
    if (!this.isMoney) {
      console.error("[toMajor] Parameters muse be Money type. ", money)
      return money
    }
    money = this.#_parseMoney(money)
    const { cent, precision, round } = this.#_findCurrency(money.currency)
    const finalAmount = this.convertAmount(money.amount, {
      precision,
      cent,
      round,
    }) // !!! 精度 + 辅币模式
    const ans = finalAmount / Math.pow(10, precision)
    if (!isNumber(ans)) {
      throw new Error(
        "[toMajor] Money amount must be Number " + JSON.stringify(money)
      )
    }
    return ans
  }
  symbol(money?: TMoney | string) {
    if (money == undefined) return this.#_money.symbol
    const { currency } = this.#_parseMoney(money)
    return this.#_findCurrency(currency)?.symbol
  }
  currency(money?: TMoney | string) {
    if (money == undefined) return this.#_money.currency
    const { currency } = this.#_parseMoney(money)
    return this.#_findCurrency(currency)?.currency
  }
  locale(money?: TMoney | string) {
    if (money == undefined) return this.#_money.locale
    const { currency } = this.#_parseMoney(money)
    return this.#_findCurrency(currency)?.locale
  }
  // 计算相关
  /** *
   * 加
   * @param hpm
   */
  sum(a: TMoneyValue, b: TMoneyValue, hpm = true) {
    // const time = new Date()?.valueOf()
    try {
      const { amount, currency } = this.#_performOperation("add", a, b, hpm)
      if (hpm) {
        return this.of(amount, currency)
        // const res = this.of(amount, currency)
        // return res
      }
      // const count = new Date().valueOf() - time
      // count > 0 && console.log("[sum] 耗时 >>>>>>>>>>>", count + "ms")
      return { amount, currency }
    } catch (e) {
      throw new Error("[add] " + e)
    }
  }
  /** *
   * 减
   */
  subtract(a: TMoneyValue, b: TMoneyValue, hpm = true) {
    // const time = new Date()?.valueOf()
    try {
      const { amount, currency } = this.#_performOperation(
        "subtract",
        a,
        b,
        hpm
      )
      if (hpm) {
        // const res = this.of(amount, currency)
        // return res
        return this.of(amount, currency)
      }
      // const count = new Date().valueOf() - time
      // count > 0 && console.log("[subtract] 耗时 >>>>>>>>>>>", count + "ms")
      return { amount, currency }
    } catch (e) {
      throw new Error("[subtract] " + e)
    }
  }
  /** *
   * 乘
   */
  multiply(
    money: TMoney | string,
    multiplier: number,
    mode = "round",
    hpm = true
  ) {
    try {
      // const time = new Date()?.valueOf()
      if (!isNumber(multiplier))
        throw new Error(
          "The multiplier value must be of Number type. " + multiplier
        )
      money = this.#_parseMoney(money, hpm)
      const { currency, amount } = money
      const { round } = this.#_findCurrency(currency)
      let ans = FloatMath.multiply(amount, multiplier)
      ans = this.processNumber(ans, mode as TRound, round)
      // const res = this.of(ans, currency)
      // const count = new Date().valueOf() - time
      // count > 0 && console.log("[multiply] 耗时 >>>>>>>>>>>", count + "ms")
      if (hpm) {
        return this.of(ans, currency)
      }
      return { amount: ans, currency }
      // return res
    } catch (e) {
      throw new Error("[multiply] " + (e?.message || JSON.stringify(e)))
    }
  }
  /** *
   * 除
   */
  divide(money: TMoney | string, divisor: number, mode = "round", hpm = true) {
    try {
      // const time = new Date()?.valueOf()
      if (!isNumber(divisor)) {
        throw new Error("divisor values must be number type. " + divisor)
      }

      money = this.#_parseMoney(money, hpm)
      const { amount, currency } = money
      if (divisor === 0) throw new Error("Cannot divide by zero. ")
      let ans = FloatMath.divide(amount, divisor)
      const { round } = this.#_findCurrency(currency)
      ans = this.processNumber(ans, mode as TRound, round)
      // const res = this.of(ans, currency)
      // const count = new Date().valueOf() - time
      // count > 0 && console.log("[divide] 耗时 >>>>>>>>>>>", count + "ms")
      if (hpm) {
        return this.of(ans, currency)
      }
      return { amount: ans, currency }
      // return res
    } catch (e) {
      throw new Error("[divide] " + (e?.message || JSON.stringify(e)))
    }
  }

  min(money1: string | TMoney, money2: string | TMoney) {
    try {
      const ans = this.compare(money1, money2)
      if (ans === 0) return money1
      else if (ans === -1) return money1
      else if (ans === 1) return money2

      console.error("[min] Invalid money data. ", money1, money2)

    } catch (e) {
      console.error("[min] error", e)
    }
  }
  max(money1: string | TMoney, money2: string | TMoney) {
    try {
      const ans = this.compare(money1, money2)
      if (ans === 0) return money1
      else if (ans === -1) return money2
      else if (ans === 1) return money1

      console.error("[max] Invalid money data. ", money1, money2)

    } catch (e) {
      console.error("[max] error", e)
    }
  }

  /** *
   * 求和等分
   */
  avg(...moneys: TMoney[]) {
    try {
      if (!moneys?.length) throw new Error("parameter cannot be empty.")
      if (moneys?.length === 1) return moneys[0]
      const [firstPart, ...args] = moneys
      let ans = 0
      args.forEach((m) => {
        ans += this.#_performOperation("add", firstPart, m).amount
      })
      const { round } = this.#_findCurrency(firstPart.currency)
      let avgAmount = FloatMath.divide(ans, moneys.length)
      avgAmount = this.processNumber(avgAmount, "round", round)
      return this.of(avgAmount, firstPart.currency)
    } catch (e) {
      throw new Error("[avg] " + e)
    }
  }
  /** *
   * 拆分，余数归最后一组
   */
  distribution(money: TMoney | string, parts?: number) {
    money = this.#_parseMoney(money)
    if (!parts || parts === 1) {
      return money
    }
    const { amount, currency } = money
    const partAmount = Math.floor(amount / parts)
    const remainder = amount - partAmount * parts
    const amounts = new Array(parts).fill(partAmount)
    // amounts[parts - 1] += remainder
    for (let i = 0; i < remainder; i++) {
      amounts[i] += 1
    }
    return amounts.map((amount) => {
      return this.of(amount, currency)
    })
  }

  /**
   * 向上取整（非四舍五入）
   * @param amount 
   * @param base 从哪位下标开始取整，以0为边界区分整数和小数；如-1表示从小数分位开始取整，1表示从整数个位开始取整
   * @param currency 
   * @returns 
   */
  ceil(amount: TMoney | string, base = 0, currency?: TCurrency) {
    try {
      const money = this.#_parseMoney(amount)
      if (!this.isMoney(money))
        throw new Error("[ceil] amount must be Money. " + money)

      if (!isInt(base)) return money
      if (money.amount === 0 || money.amount < 0 || base === 0 || base < 0)
        return money
      currency = money.currency
      const _amount = this.toMajor(money) as number

      const ans = this.#_calculateBase(_amount, base, "ceil")
      return this.ofMajor(ans, currency)
    } catch (e) {
      console.error("[ceil] error", e)
    }
  }
  /** *
   * 向下凑整（非四舍五入）
   */
  floor(amount: TMoney | string, base = 1, currency?: TCurrency) {
    try {
      const money = this.#_parseMoney(amount)
      if (!this.isMoney(money))
        throw new Error("[floor] amount must be Money. " + money)

      if (!isInt(base)) return money
      if (money.amount === 0 || money.amount < 0 || base === 0 || base < 0)
        return money
      currency = money.currency
      const _amount = this.toMajor(money) as number
      const ans = this.#_calculateBase(_amount, base, "floor")
      return this.ofMajor(ans, currency)
    } catch (e) {
      throw new Error("[floor] error" + e)
    }
  }
  /** *
   * 获取当前货币配置
   */
  getContext(currency = this.#_money.currency) {
    if (!this.#_moneyConfig[currency]) {
      console.error("[getContext] could not found " + currency + " from config")
      return
    }
    return this.#_moneyConfig[currency]
  }
  /**
   * 修改当前货币配置
   */
  setContext(currency: TCurrency) {
    if (!this.#_moneyConfig[currency]) return false
    this.#_money = this.#_moneyConfig[currency]
    return true
  }
}

export default Money
